import { paths } from '@mendikot/protocol';
import { Component, lazy, Suspense, useEffect, type ErrorInfo, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';
import { toast, Toaster } from 'sonner';
import { Ambient } from '../components/effects/effects';
import { Button } from '../components/ui/primitives';
import { useReduceMotion } from '../hooks/useMotionPrefs';
import { firebaseEnabled, loadOnline } from '../platform/firebase';
import { Home } from '../screens/Home';
import { needsWelcome, WelcomeGate } from '../screens/prompts';
import type { WebServices } from '../platform/services';
import { useAuthUser, useServices, useSettings, useT } from './context';

// Route-level code splitting keeps the first paint light on low-end phones.
const named = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const Settings = named(() => import('../screens/Account'), 'Settings');
const Profile = named(() => import('../screens/Account'), 'Profile');
const HowToPlay = named(() => import('../screens/Account'), 'HowToPlay');
const CreateRoom = named(() => import('../screens/Online'), 'CreateRoom');
const JoinRoom = named(() => import('../screens/Online'), 'JoinRoom');
const OnlineRoom = named(() => import('../screens/Online'), 'OnlineRoom');
const PracticeSetup = named(() => import('../screens/Practice'), 'PracticeSetup');
const PracticeTable = named(() => import('../screens/Practice'), 'PracticeTable');
const Privacy = named(() => import('../screens/Privacy'), 'Privacy');
import { useTheme } from './theme';

function Layout() {
  const theme = useTheme();
  const reduce = useReduceMotion();
  const services = useServices();
  const user = useAuthUser();
  const navigate = useNavigate();
  const lang = useSettings((s) => s.lang);
  const name = useSettings((s) => s.name);
  const avatarId = useSettings((s) => s.avatarId);
  const welcome = useSettings(needsWelcome);
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Identity for analytics and crash reports: pseudonymous uid only, never email.
  useEffect(() => {
    services.analytics.setUserId(user?.uid ?? null);
    services.crash.setUser(user?.uid ?? null);
    if (user) services.analytics.setUserProperties({ auth_method: user.providers[0] ?? 'guest' });
  }, [user, services]);

  // Mirror the public profile to RTDB (validated by security rules).
  useEffect(() => {
    if (!firebaseEnabled || !user || !name) return;
    const id = setTimeout(() => {
      void Promise.all([loadOnline(), import('firebase/database')])
        .then(([{ db }, { ref, serverTimestamp, set }]) =>
          set(ref(db, paths.userProfile(user.uid)), {
            name,
            avatarId,
            lang,
            updatedAt: serverTimestamp(),
          }),
        )
        .catch(() => undefined);
    }, 800);
    return () => clearTimeout(id);
  }, [user, name, avatarId, lang]);

  // Foreground pushes become toasts; tapping opens the deep link.
  useEffect(
    () =>
      services.push.onForeground((push) => {
        services.analytics.log('notification_foreground', { type: push.type ?? 'unknown' });
        toast(push.title ?? 'Mendikot', {
          description: push.body,
          action: push.link ? { label: 'Open', onClick: () => navigate(push.link!) } : undefined,
        });
      }),
    [services, navigate],
  );

  return (
    <div className="app-shell relative">
      <Ambient theme={theme} reduce={reduce} />
      <div className="relative z-10">
        {/* DPDP: no screen (and so no account) until the player is 18+ and has seen the notice. */}
        {welcome && pathname !== '/privacy' ? (
          <WelcomeGate />
        ) : (
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        )}
      </div>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: 'var(--c-surface-raised)',
            color: 'var(--c-text)',
            border: '1px solid var(--c-border)',
          },
        }}
      />
    </div>
  );
}

function RouteError() {
  const t = useT();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-ink-muted">{t('error.default')}</p>
      <Button onClick={() => window.location.assign('/')}>{t('match.home')}</Button>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Home /> },
      { path: 'play/new', element: <CreateRoom /> },
      { path: 'join', element: <JoinRoom /> },
      { path: 'r/:code', element: <JoinRoom /> },
      { path: 'room/:roomId', element: <OnlineRoom /> },
      { path: 'practice/new', element: <PracticeSetup /> },
      { path: 'practice', element: <PracticeTable /> },
      { path: 'how-to-play', element: <HowToPlay /> },
      { path: 'settings', element: <Settings /> },
      { path: 'profile', element: <Profile /> },
      { path: 'privacy', element: <Privacy /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

function CrashFallback() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg p-6 text-center text-ink">
      <p>Something went wrong. The error has been reported.</p>
      <button
        className="rounded-2xl bg-accent px-4 py-2 font-bold text-accent-ink"
        onClick={() => window.location.assign('/')}
      >
        Reload
      </button>
    </div>
  );
}

/** Top-level boundary: reports through the CrashReporter (Sentry on web, Crashlytics on native). */
class CrashBoundary extends Component<
  { services: WebServices; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.services.crash.captureError(error, {
      where: 'react_boundary',
      component_stack: info.componentStack?.slice(0, 300) ?? null,
    });
  }
  override render() {
    return this.state.failed ? <CrashFallback /> : this.props.children;
  }
}

export function App() {
  const services = useServices();
  return (
    <CrashBoundary services={services}>
      <RouterProvider router={router} />
    </CrashBoundary>
  );
}
